import flow from "lodash/fp/flow";
import find from "lodash/fp/find";
import isObject from "lodash/fp/isObject";
import round from "lodash/fp/round";
import isNil from "lodash/fp/isNil";
import isEmpty from "lodash/fp/isEmpty";
import identity from "lodash/fp/identity";
import assign from "lodash/fp/assign";
import head from "lodash/fp/head";
import moment from "moment";
import constants from "../../core/constants";
import { toObject, timeDiff } from "../../core/utils";

const formatDate = (schedule) => {
  return schedule ? moment(schedule).format(constants.dateTimeFormat) : null;
};

const getTimingStatus = (actual, expected) => {
  let timingStatus;
  if (actual) {
    const timeDifference = timeDiff(actual, expected);
    const timeDiffMsg = `${timeDifference.isAfter ? "late" : "early"} ${
      timeDifference.time.message
    }`;
    timingStatus = {
      isLate: timeDifference.isAfter,
      message: `Bus arrived ${
        timeDifference.isInTime ? "in time" : timeDiffMsg
      }`,
      iconName: timeDifference.isAfter ? "clear" : "check",
      diff: timeDifference.time.message,
    };
  } else {
    timingStatus = {
      message:
        "Bus expected to arrive at " +
        moment(expected).format(constants.timeFormat),
      iconName: "access_time",
    };
  }
  return timingStatus;
};

class RidePageTransform {
  constructor($injector, Lists, Events) {
    "ngInject";

    this.Lists = Lists.all;
    this.RidesTransform = $injector.get("RidesTransforms");
    this.rideStatus = [
      { name: "stopped", title: "Stopped" },
      { name: "started", title: "Started" },
      { name: "in-progress", title: "In Progress" },
      { name: "scheduled", title: "Scheduled" },
      { name: "completed", title: "Completed" },
      { name: "cancelled", title: "cancelled" },
      { name: "corporate_revised", title: "Corporate Revised" },
    ];
    this.onSelectBusTypeEvent = Events.getSelectBusEvent();
    this.getManualRideOptions = this.getManualRideOptions.bind(this);
    Events.on(
      this.onSelectBusTypeEvent,
      this.applyContractRidePrice.bind(this)
    );
  }

  getManualRideOptions(ride, activeContract) {
    this.ride = ride;
    this.activeContract = activeContract;
    this.manualRideFields = {
      title: "Add New Manual Ride",
      icon: "add",
      fields: [
        {
          resource: "Corporate",
          name: "corporate",
          title: "Corporate",
          value: ride.corporates[0],
          isResource: true,
          isDisabled: true,
          renderIf: true,
        },
        {
          name: "ride_category",
          title: "Ride Category",
          value: ride.ride_category._id,
          list: "rideCategoryList",
          isDisabled: true,
          renderIf: true,
        },
        {
          resource: "Route",
          name: "route",
          value: ride.route,
          title: "Route",
          isResource: true,
          isDisabled: true,
          renderIf: true,
        },
        {
          resource: "Bustype",
          name: "bus_type",
          value: ride.bus_type,
          title: "Bustype",
          isResource: true,
          onChange: this.onSelectBusTypeEvent,
          isRequired: true,
          renderIf: true,
        },
        {
          name: "ride_price",
          title: "Ride Price",
          type: "number",
          isText: true,
          isRequired: true,
          renderIf: true,
        },
        {
          resource: "Captain",
          name: "captain",
          title: "Captain",
          value: ride.captain,
          isResource: true,
          isRequired: false,
          hasExact: true,
          exactFields: ["name", "phone"],
          renderIf: true,
        },
        {
          resource: "Bus",
          name: "bus",
          title: "Bus",
          value: ride.bus,
          isListResource: true,
          isRequired: false,
          requires: "captain",
          listTransformer: "BusesTransforms",
          renderIf: true,
        },
        {
          name: "date",
          value: ride.date.value,
          title: "Date",
          dateTime: true,
          isRequired: true,
          minDate: moment(),
          renderIf: true,
        },
      ],
    };
    return this.manualRideFields;
  }

  justify(data, isFlat) {
    // IsFlat determine whether to use toObject or not
    const format = (item) => {
      item.captain = item.captain || {};
      item.hasETA = data.on_start_schedule.length > 0;
      item.status = isObject(item.status)
        ? item.status
        : find((i) => i.name === item.status)(this.rideStatus);
      item.bus = item.bus ? assign(item.bus, { name: item.bus.plates }) : null;
      item.priceNum = item.price;
      item.price /= constants.currencyBy;
      item.is_dynamically_priced = !isEmpty(item.pricing_parameters);
      if (item.pricing_parameters) {
        item.pricing_parameters.base_fare /= constants.currencyBy;
        item.pricing_parameters.maximum_fare /= constants.currencyBy;
        item.pricing_parameters.time_bucket_fare /= constants.currencyBy;
        item.pricing_parameters.distance_bucket_fare /= constants.currencyBy;
      }
      if (isNil(item.captain_price)) {
        item.captain_price = null;
      } else {
        item.captain_price /= constants.currencyBy;
      }

      if (!isNil(item.ride_category)) {
        item.ride_category.label = `${item.ride_category.ride_type} ${item.ride_category.service_type}`;
      }
      item.arrival_time_allowance /= constants.fromMinToMilli;
      item.departure_time_allowance /= constants.fromMinToMilli;
      item.date = item.date
        ? moment(item.date).clone().format(constants.dateTimeFormat)
        : null;
      item.emergencyDate = item.emergency_date
        ? moment(item.emergency_date).format(constants.dateTimeFormat)
        : null;
      item.stationsSchedules = this.mapSchedule(item);

      item.totalDistance = `${round(item.route.total_distance / 1000)}KM`;
      if (!isNil(item.corporates) && !isEmpty(item.corporates)) {
        item.corporate = head(item.corporates);
      }

      item.confirmed_at =
        item.confirmed && item.confirmed_at && formatDate(item.confirmed_at);
      item.createdAt = item.createdAt && formatDate(item.createdAt);

      return item;
    };

    return isFlat
      ? flow([format, identity])(data)
      : toObject(flow([format, identity])(data));
  }

  getCashCollectionSum(data) {
    return data.reduce((acc, current) => {
      return acc + Math.abs(current.transaction.totalAmount);
    }, 0);
  }

  applyContractRidePrice(selectedBusType) {
    if (isNil(this.ride) || isNil(this.activeContract)) return;
    const route = this.ride.route;
    // Prefill or get the selected bustype
    const busType =
      selectedBusType ||
      this.manualRideFields.fields.find(
        (field) => field.name === "bus_type" || field.name === "lumpsum"
      ).value;
    if (isNil(busType)) return;
    const ridePrice = getContractRidePrice(busType, route, this.activeContract);
    this.manualRideFields.fields.forEach((field) => {
      if (field.name === "bus_type" && field.name === "lumpsum") {
        if (!ridePrice) {
          field.disclaimer =
            'This configuration does not exist in the corporate contract and will be treated as an "Out-of-contract Special Request". \n Please enter the ride price for the corporate invoice';
        } else if (field.disclaimer) delete field.disclaimer;
      }
      if (field.name === "ride_price") {
        if (isNil(busType)) {
          field.value = "";
          field.isDisabled = false;
        } else if (!ridePrice) {
          field.value = "";
          field.isDisabled = false;
        } else {
          field.value = ridePrice.pricing.ride_price / constants.currencyBy;
          field.isDisabled = true;
        }
      }
    });
  }

  mapSchedule(item) {
    const arrivals = item.arrivals;
    const departures = item.departures || [];
    const etas = item.on_start_schedule || [];
    const stations = item.stations_data;
    const schedules = item.schedule;

    return schedules.map((s, i) => {
      const stationInstance = stations[i];
      if (isNil(stationInstance)) return false;
      if (i === 0) {
        stationInstance.distanceFromBase = 0;
      } else {
        const previousStationInstance = stations[i - 1];
        stationInstance.distanceFromBase = parseFloat(
          (
            stationInstance.distance / 1000 +
            previousStationInstance.distanceFromBase
          ).toFixed(1)
        );
      }

      const isPast = stationInstance.status === "past";
      const isSkipped = stationInstance.status === "skipped";
      const isUpcoming = stationInstance.status === "coming";
      const expected = formatDate(s);
      const actual = isSkipped ? "Skipped" : formatDate(arrivals.shift());
      const departure = formatDate(departures[i]);
      const eta = formatDate(etas[i]);
      const timingStatus = getTimingStatus(actual, expected);
      const isLoadingETA = false;

      return {
        expected,
        actual,
        stationInstance,
        departure,
        eta,
        isSkipped,
        isPast,
        isUpcoming,
        timingStatus,
        isLoadingETA,
      };
    });
  }
}

export function getContractRidePrice(busType, route, activeContract) {
  const ridePrice = activeContract.subscriptions.find((sub) => {
    if (
      activeContract.contract_type === "busType" ||
      activeContract.contract_type === "lumpsum"
    ) {
      return sub.bus_type._id === busType._id;
    }
    return sub.bus_type._id === busType._id && sub.route._id === route._id;
  });
  return ridePrice;
}

export default RidePageTransform;



// WEBPACK FOOTER //
// ./src/pages/ride/ride.transforms.js